import React, { useState, useEffect, useMemo } from 'react';
import { FormControl } from '@mui/material';
import CurrencyTextField from 'components/CurrencyTextFieldCompat';
import clsx from 'clsx';
import persianJs from 'persianjs';
import { phrases, validator } from 'myValidation';
import { FieldWrapper, NumberToPersianWords } from 'components';
import { useStyles } from './Style';
import { useCurrencyUnit } from 'hooks';
import { Controller } from 'react-hook-form';

const InputCurrency = (props) => {
  const {
    name,
    defaultValue = '',
    rules,
    label,
    control,
    onChangeValue,
    mainStyle = {},
    englishNumber = true,
    hasMargin = true,
    showConvertToWords = false,
    forceValue,
    ...rest
  } = props;

  const classes = useStyles();
  const currencyText = useCurrencyUnit(true);

  // در صورتی که checkConfig آماده نباشد، رندر اولیه را به تعویق می‌اندازیم
  const [checkConfig, setCheckConfig] = useState(null);

  useEffect(() => {
    if (currencyText) {
      setCheckConfig(currencyText.newCurrencyFormat);
    }    
  }, [currencyText]);

  const fieldLabel = useMemo(() => {
    if (label) return label;

    let nameField = name || '';
    if (nameField.includes('[')) nameField = nameField.split('[')[0];
    if (nameField.includes('.')) nameField = `${nameField}.${nameField.split('.').pop()}`;

    return phrases.attributes[nameField.replace(/\s*\[.*?\]\s*/g, '')] || name;
  }, [label, name]);

  const validationRules = useMemo(() => {
    const result = { validate: {} };

    if (rules && Array.isArray(rules)) {
      rules.forEach((item) => {
        if (item.includes('|')) {
          let [funcName, options] = item.split('|');
          const func = validator.validators[funcName];

          if (typeof options === 'string' && options.includes('{')) {
            options = JSON.parse(options);
          }

          result.validate[item] = (value) => {
            if (!func(value, options)) {
              return phrases[funcName]
                .replace(':attribute', fieldLabel)
                .replace(':param', options);
            }
            return true;
          };
        } else {
          const func = validator.validators[item];
          result.validate[item] = (value) =>
           !func(String(value ?? ''))
              ? phrases[item].replace(':attribute', fieldLabel)
              : true;
        }
      });
    }

    return result;
  }, [rules, fieldLabel]);

  const handleCurrencyChange = (value) => {
    let data = !value
      ? value
      : englishNumber
      ? persianJs(value.toString()).toEnglishNumber().toString()
      : value;

    if (!checkConfig && data) {
      data = data.split('.')[0];
    }

    return data;
  };

  const renderCurrencyField = ({ value, onChange }) => (
    <CurrencyTextField
      {...rest}
      key={checkConfig ? 'decimal-4' : 'decimal-0'}
      modifyValueOnWheel={false}
      label={null}
      value={forceValue !== undefined ? forceValue : value ?? ''}
      onChange={(event, val) => {
        const cleanedValue = handleCurrencyChange(val);

        if (onChange) {
          onChange(cleanedValue);
        }

        if (onChangeValue) {
          onChangeValue(cleanedValue);
        }
      }}
      className={classes.currencyInput}
      decimalCharacter="."
      decimalPlaces={checkConfig ? 4 : 0}
      allowDecimalPadding={false}
      variant="outlined"
      currencySymbol={
        <div className={classes.currencyText}>
          {currencyText?.currencyLabel}
        </div>
      }
      outputFormat="string"
      dir="ltr"
      autoComplete="off"
    />
  );

  if (checkConfig === null) return null;

  return (
    <FieldWrapper {...props} label={label}>
      <FormControl
        className={clsx({ [classes.margin]: hasMargin })}
        style={{ width: '100%', ...mainStyle }}
      >
        {control ? (
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={validationRules}
            // اصلاح شده برای React Hook Form v6 (مستقیماً از پارامترهای ورودی رندر استفاده می‌شود)
            render={({ field: { onChange, value } }) => {
              const currentValue = forceValue !== undefined ? forceValue : value;
              return (
                <>
                  {renderCurrencyField({
                    value: currentValue,
                    onChange,
                  })}
                  {showConvertToWords && (
                    <NumberToPersianWords number={currentValue} />
                  )}
                </>
              );
            }}
          />
        ) : (
          <>
            {renderCurrencyField({
              value: forceValue ?? defaultValue,
              onChange: null,
            })}
            {showConvertToWords && (
              <NumberToPersianWords number={forceValue ?? defaultValue} />
            )}
          </>
        )}
      </FormControl>
    </FieldWrapper>
  );
};

export default InputCurrency;
